<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useFiberMonitoringStore } from '@/stores/useFiberMonitoringStore'
import { searchFiberLinks } from '@/api/fiberMonitoringApi'
import TimeSeriesChart from '@/components/data-visualization/charts/TimeSeriesChart.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
import type { FiberLinkSearchHit, LinkRange, WeatherStatus } from '@/types/fiberMonitoring'

const route = useRoute()
const router = useRouter()
const store = useFiberMonitoringStore()
const {
  link,
  statusHistory,
  throughput,
  events,
  availability,
  range,
  loadingLink,
  loadingCharts,
  linkError,
  chartsError,
  syncing,
  lastSyncResult,
} = storeToRefs(store)

const search = ref('')
const suggestions = ref<FiberLinkSearchHit[]>([])
const showSuggestions = ref(false)
const searchLoading = ref(false)
let searchDebounce: ReturnType<typeof setTimeout> | null = null
let searchToken = 0

function fetchSuggestions(q: string) {
  const token = ++searchToken
  searchLoading.value = true
  searchFiberLinks(q, 20)
    .then((hits) => {
      // Ignore les réponses arrivées en retard sur une frappe plus récente.
      if (token !== searchToken) return
      suggestions.value = hits
    })
    .catch(() => {
      if (token === searchToken) suggestions.value = []
    })
    .finally(() => {
      if (token === searchToken) searchLoading.value = false
    })
}

watch(search, (val) => {
  if (searchDebounce) clearTimeout(searchDebounce)
  // Lance la recherche dès qu'on a au moins un caractère (vide = liste initiale).
  searchDebounce = setTimeout(() => fetchSuggestions(val.trim()), 300)
})

function onFocus() {
  showSuggestions.value = true
  if (suggestions.value.length === 0) fetchSuggestions(search.value.trim())
}

function onBlur() {
  // Léger délai pour laisser passer le mousedown sur une suggestion.
  setTimeout(() => (showSuggestions.value = false), 150)
}

function selectSuggestion(hit: FiberLinkSearchHit) {
  showSuggestions.value = false
  router.push(`/admin/fiber-links/${encodeURIComponent(hit.externalReference)}`)
}

onBeforeUnmount(() => {
  if (searchDebounce) clearTimeout(searchDebounce)
})

const RANGES: { value: LinkRange; label: string }[] = [
  { value: '1d', label: '1 jour' },
  { value: '7d', label: '7 jours' },
  { value: '30d', label: '30 jours' },
  { value: '90d', label: '90 jours' },
]

const codeContrat = computed(() => (route.params.codeContrat as string | undefined) ?? '')

function submitSearch() {
  const code = search.value.trim()
  if (!code) return
  router.push(`/admin/fiber-links/${encodeURIComponent(code)}`)
}

function selectRange(r: LinkRange) {
  if (codeContrat.value) store.setRange(codeContrat.value, r)
}

const RANGE_DAYS: Record<LinkRange, number> = { '1d': 1, '7d': 7, '30d': 30, '90d': 90 }

async function onSync() {
  if (!codeContrat.value) return
  // Aligne le backfill sur la plage affichée : si la vue est en 90j on tire 90 jours,
  // si on est en 1j c'est juste un refresh récent.
  await store.syncLink(codeContrat.value, RANGE_DAYS[range.value])
}

watch(
  codeContrat,
  (code) => {
    if (code) {
      search.value = code
      store.load(code)
    } else {
      store.reset()
    }
  },
  { immediate: true },
)

// -- Formatage --------------------------------------------------------------

function formatBitsPerSec(v: number): string {
  if (!Number.isFinite(v)) return '—'
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)} Gb/s`
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} Mb/s`
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)} kb/s`
  return `${v.toFixed(0)} b/s`
}

function formatStatus(v: number): string {
  return v === 1 ? 'UP' : v === 0 ? 'DOWN' : ''
}

function formatDate(epochSeconds: number | null): string | null {
  if (!epochSeconds) return null
  return new Date(epochSeconds * 1000).toLocaleString('fr-FR')
}

function formatPct(ratio: number): string {
  return `${(ratio * 100).toFixed(2)} %`
}

function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const parts: string[] = []
  if (days) parts.push(`${days}j`)
  if (hours) parts.push(`${hours}h`)
  if (minutes || (!days && !hours)) parts.push(`${minutes}m`)
  return parts.join(' ')
}

const statusSeries = computed(() => [
  {
    name: 'État',
    step: true,
    color: '#22c55e',
    points: statusHistory.value?.points ?? [],
  },
])

const throughputSeries = computed(() => [
  { name: 'Download', color: '#2563eb', points: throughput.value?.download ?? [] },
  { name: 'Upload', color: '#f59e0b', points: throughput.value?.upload ?? [] },
])

// -- Badge météo ------------------------------------------------------------

const WEATHER_LABEL: Record<WeatherStatus, string> = {
  OK: 'OK',
  WARNING: 'Bagot',
  KO: 'KO',
  UNKNOWN: 'Inconnu',
}

const WEATHER_BADGE: Record<WeatherStatus, string> = {
  OK: 'badge-success',
  WARNING: 'badge-warning',
  KO: 'badge-error',
  UNKNOWN: 'badge-ghost',
}

// Libellé du temps : « Connecté depuis le X » si UP, « Déconnecté depuis le X » si DOWN.
const connectionLabel = computed(() => {
  if (!link.value || link.value.status === null) return 'Temps de connexion'
  return link.value.status === 'UP' ? 'Connecté depuis' : 'Déconnecté depuis'
})
</script>

<template>
  <div class="p-4 max-w-6xl mx-auto space-y-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-1">
        <h1 class="text-2xl font-bold">Monitoring lien fibre</h1>
        <HelpButton />
      </div>
      <div class="relative w-full sm:w-96">
        <form class="join w-full" @submit.prevent="submitSearch">
          <input
            v-model="search"
            type="text"
            placeholder="Société, nom, code contrat…"
            class="input input-bordered join-item w-full"
            @focus="onFocus"
            @blur="onBlur"
          />
          <button type="submit" class="btn btn-primary join-item">OK</button>
        </form>
        <ul
          v-if="showSuggestions && (suggestions.length > 0 || searchLoading)"
          class="menu bg-base-100 rounded-box shadow-lg absolute z-20 mt-1 w-full max-h-80 overflow-auto p-2"
        >
          <li v-if="searchLoading && suggestions.length === 0" class="px-3 py-2 text-sm opacity-60">
            Recherche…
          </li>
          <li v-for="hit in suggestions" :key="hit.uuid">
            <a class="flex justify-between gap-2" @mousedown.prevent="selectSuggestion(hit)">
              <div class="flex flex-col min-w-0">
                <span class="font-medium truncate">{{ hit.companyName }} — {{ hit.name }}</span>
                <span class="font-mono text-xs opacity-60">{{ hit.externalReference }}</span>
              </div>
              <span
                v-if="hit.weather"
                class="badge badge-sm shrink-0"
                :class="WEATHER_BADGE[hit.weather]"
              >
                {{ WEATHER_LABEL[hit.weather] }}
              </span>
            </a>
          </li>
        </ul>
      </div>
    </div>

    <div v-if="!codeContrat" class="alert">
      <span>Saisis un code contrat pour afficher l'état et les graphiques d'un lien.</span>
    </div>

    <template v-else>
      <div v-if="linkError" class="alert alert-error">
        <span>{{ linkError }}</span>
      </div>

      <div v-if="loadingLink" class="skeleton h-28 w-full"></div>
      <div v-else-if="link" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <span class="text-sm opacity-60">État</span>
            <div class="flex items-center gap-2">
              <span
                class="badge badge-lg"
                :class="{
                  'badge-success': link.status === 'UP',
                  'badge-error': link.status === 'DOWN',
                  'badge-ghost': link.status === null,
                }"
              >
                {{ link.status ?? 'Inconnu' }}
              </span>
              <span class="badge" :class="WEATHER_BADGE[link.weather]">
                {{ WEATHER_LABEL[link.weather] }}
              </span>
            </div>
            <span v-if="link.lastSyncedAt" class="text-xs opacity-50">
              Sync : {{ formatDate(link.lastSyncedAt) }}
            </span>
          </div>
        </div>

        <div class="card bg-base-100 shadow">
          <div class="card-body gap-2">
            <div>
              <span class="text-sm opacity-60 block">Adresse IP</span>
              <span class="text-base font-mono">{{ link.ip ?? '—' }}</span>
            </div>
            <div>
              <span class="text-sm opacity-60 block">Réseaux IPv4 routés</span>
              <span class="text-base font-mono">{{ link.reseaux ?? '—' }}</span>
            </div>
          </div>
        </div>

        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <span class="text-sm opacity-60">{{ connectionLabel }}</span>
            <span class="text-lg">{{ link.connection.human ?? '—' }}</span>
            <span v-if="formatDate(link.connection.since)" class="text-xs opacity-50">
              {{ formatDate(link.connection.since) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Détails identité -->
      <div v-if="link" class="card bg-base-100 shadow">
        <div class="card-body grid gap-2 sm:grid-cols-2 text-sm">
          <div><span class="opacity-60">Code contrat :</span> {{ link.codeContrat }}</div>
          <div><span class="opacity-60">Device :</span> {{ link.identity.device }}</div>
          <div><span class="opacity-60">VISP :</span> {{ link.identity.visp }}</div>
          <div><span class="opacity-60">Cluster :</span> {{ link.identity.cluster ?? '—' }}</div>
          <div><span class="opacity-60">Login :</span> {{ link.identity.login ?? '—' }}</div>
          <div class="sm:col-span-2">
            <span class="opacity-60">Commentaire :</span> {{ link.identity.comment ?? '—' }}
          </div>
        </div>
      </div>

      <!-- Sélecteur de plage -->
      <div class="join">
        <button
          v-for="r in RANGES"
          :key="r.value"
          class="btn join-item"
          :class="{ 'btn-active btn-primary': range === r.value }"
          @click="selectRange(r.value)"
        >
          {{ r.label }}
        </button>
      </div>

      <!-- KPI disponibilité -->
      <div v-if="availability" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <span class="text-sm opacity-60">Dispo globale</span>
            <span class="text-2xl font-bold">{{ formatPct(availability.global.ratio) }}</span>
          </div>
        </div>
        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <span class="text-sm opacity-60">Dispo HO (9h-18h L-V)</span>
            <span class="text-2xl font-bold">{{ formatPct(availability.ho.ratio) }}</span>
          </div>
        </div>
        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <span class="text-sm opacity-60">Dispo HNO</span>
            <span class="text-2xl font-bold">{{ formatPct(availability.hno.ratio) }}</span>
          </div>
        </div>
        <div class="card bg-base-100 shadow">
          <div class="card-body">
            <span class="text-sm opacity-60">Coupures</span>
            <span class="text-2xl font-bold">{{ availability.outagesCount }}</span>
            <span v-if="availability.longestOutageSeconds > 0" class="text-xs opacity-50">
              + longue : {{ formatDuration(availability.longestOutageSeconds) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Graphiques -->
      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <TimeSeriesChart
            title="Statut de connexion"
            :series="statusSeries"
            :loading="loadingCharts"
            :error="chartsError"
            :y-min="0"
            :y-max="1"
            :value-formatter="formatStatus"
          />
        </div>
      </div>

      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <TimeSeriesChart
            title="Débit download / upload"
            :series="throughputSeries"
            :loading="loadingCharts"
            :error="chartsError"
            :y-min="0"
            :value-formatter="formatBitsPerSec"
          />
        </div>
      </div>

      <!-- Liste des événements -->
      <div class="card bg-base-100 shadow">
        <div class="card-body">
          <div class="flex items-center justify-between mb-2">
            <h3 class="font-semibold">Historique des événements</h3>
            <button
              class="btn btn-sm btn-outline"
              :disabled="syncing"
              @click="onSync"
              :title="`Récupère les ${RANGE_DAYS[range]} derniers jours d'historique depuis Thanos pour ce lien`"
            >
              <span v-if="syncing" class="loading loading-spinner loading-xs"></span>
              {{ syncing ? 'Synchronisation…' : `Synchroniser (${RANGE_DAYS[range]}j)` }}
            </button>
          </div>
          <div
            v-if="lastSyncResult"
            class="text-xs opacity-60 mb-2"
          >
            Dernière synchro : {{ lastSyncResult.eventsInserted }} nouvel(les) transition(s) insérée(s)
            sur {{ lastSyncResult.days }} jour(s).
          </div>
          <div v-if="!events || events.events.length === 0" class="text-center opacity-60 py-4">
            Aucun événement sur la période.
          </div>
          <div v-else class="overflow-x-auto">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Événement</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(ev, i) in events.events" :key="`${ev.at}-${i}`">
                  <td class="font-mono">{{ formatDate(ev.at) }}</td>
                  <td>
                    <span
                      class="badge"
                      :class="{
                        'badge-success': ev.type === 'UP',
                        'badge-error': ev.type === 'DOWN',
                      }"
                    >
                      {{ ev.type === 'UP' ? 'Connexion' : 'Déconnexion' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped></style>
